(() => {
  if (customElements.get('juli-upsell-card')) return;

  let pendingFocus = null;
  window.JuliUpsellFocus = {
    queue(card, origin) {
      const announcement = document.querySelector('[data-upsell-announcement]');
      if (announcement) announcement.textContent = card.dataset.added;
      if (origin && card.contains(origin)) pendingFocus = { id: card.dataset.productId, origin };
    },
    restore() {
      if (!pendingFocus) return;
      const { id, origin } = pendingFocus;
      if (origin.isConnected) { pendingFocus = null; return; }
      const active = document.activeElement;
      // Do not steal focus if the customer moved while the request ran.
      if (active && active !== document.body && active !== document.documentElement) {
        pendingFocus = null;
        return;
      }
      const drawer = document.querySelector('.juli-slidecart');
      const target = drawer?.querySelector('juli-cart-recommendations [data-open]:not(:disabled)') ||
        drawer?.querySelector(`[data-cart-product-id="${id}"] .juli-slidecart__item-details a`) ||
        drawer?.querySelector('button[name="checkout"]');
      if (target) { target.focus({ preventScroll: true }); pendingFocus = null; }
    }
  };

  // The existing compiled theme opens the drawer on every public add event.
  // Internal cart/section updates happen first. Only our inline requests skip
  // that public event; ordinary product forms keep their existing behaviour.
  // Section replacement creates a new scroll container. Keep the customer's
  // position for cart updates, including adds that increase the content height.
  const cartScroll = new WeakMap();
  document.addEventListener('liquid-ajax-cart:request-start', (event) => {
    const scroll = document.querySelector('.juli-slidecart__scroll');
    if (scroll) cartScroll.set(event.detail.requestState, scroll.scrollTop);
  });
  document.addEventListener('liquid-ajax-cart:request-end', (event) => {
    const position = cartScroll.get(event.detail.requestState);
    const scroll = document.querySelector('.juli-slidecart__scroll');
    if (scroll && position !== undefined) scroll.scrollTop = position;
    cartScroll.delete(event.detail.requestState);
    if (event.detail?.requestState?.info?.juliInlineUpsell) event.stopImmediatePropagation();
  }, true);

  function cartReady() {
    if (window.liquidAjaxCart?.init) return Promise.resolve(window.liquidAjaxCart);
    return new Promise((resolve, reject) => {
      const ready = () => { clearTimeout(timeout); resolve(window.liquidAjaxCart); };
      const timeout = setTimeout(() => {
        document.removeEventListener('liquid-ajax-cart:init', ready);
        reject(new Error('Cart unavailable'));
      }, 10000);
      document.addEventListener('liquid-ajax-cart:init', ready, { once: true });
    });
  }

  class JuliUpsellCard extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const data = this.querySelector('[data-variants]');
      if (!data) return;
      this.variants = JSON.parse(data.getAttribute('data-variants'));
      this.initialized = true;
      this.openButton = this.querySelector('[data-open]');
      this.addButton = this.openButton;
      this.summary = this.querySelector('.juli-upsell-card__summary');
      this.optionsPanel = this.querySelector('[data-options]');
      this.fields = [...this.querySelectorAll('[data-option-index]')];
      this.errorMessage = this.querySelector('[data-error-message]');
      this.status = this.querySelector('[data-status]');
      this.closeButton = this.querySelector('[data-close]');
      this.toggleButton = this.querySelector('[data-toggle]');
      this.closeButton?.addEventListener('click', () => this.closeOptions());
      this.toggleButton?.addEventListener('click', () => {
        if (this.busy) return;
        if (!this.optionsPanel.hidden) return this.closeOptions();
        this.setExpanded(true);
      });
      this.summary.addEventListener('click', event => {
        if (!event.target.closest('button, a') && !this.optionsPanel.hidden) this.closeOptions();
      });
      this.openButton.disabled = !this.variants.some(v => v.available);
      if (this.variants.length === 1) {
        this.openButton.removeAttribute('aria-expanded');
        this.openButton.removeAttribute('aria-controls');
      }
      this.openButton.addEventListener('click', () => {
        if (this.busy) return;
        if (this.variants.length === 1) return this.add(this.variants[0]);
        if (!this.optionsPanel.hidden) return this.add(this.selectedVariant());
        this.setExpanded(true);
        this.fields[0]?.querySelector('input:not(:disabled)')?.focus();
      });
      this.fields.forEach((field, index) => field.addEventListener('change', () => {
        // Keep an explicit size when it still exists for the selected colour.
        const chosen = this.selections();
        this.fields.forEach((next, nextIndex) => {
          if (nextIndex === index || next.hasAttribute('data-color-option')) return;
          const checked = next.querySelector('input:checked');
          if (checked && !this.variants.some(v => v.available && v.options[nextIndex] === checked.value &&
            this.fields.every((other, i) => !other.hasAttribute('data-color-option') || chosen[i] === null || v.options[i] === chosen[i]))) {
            checked.checked = false;
          }
        });
        this.clearError();
        this.updateOptions();
      }));
      this.optionsPanel.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.busy) {
          if (this.closest('.juli-slidecart')) event.stopPropagation();
          this.setExpanded(false);
          this.openButton.focus();
        }
      });
      this.updateOptions();
    }
    matchColor(color) {
      if (!color || this.busy) return;
      const field = this.fields.find(item => item.hasAttribute('data-color-option'));
      if (!field) return;
      const index = Number(field.dataset.optionIndex);
      const input = [...field.querySelectorAll('input')].find(item =>
        item.value.toLocaleLowerCase() === String(color).toLocaleLowerCase());
      if (!input || !this.variants.some(variant => variant.available && variant.options[index] === input.value)) return;
      input.checked = true;
      this.updateOptions();
    }
    closeOptions() {
      this.setExpanded(false);
      this.openButton.focus({ preventScroll: true });
    }
    setExpanded(expanded) {
      if (expanded) {
        this.closest('.juli-upsell')?.querySelectorAll('juli-upsell-card').forEach(card => {
          if (card !== this && card.initialized && !card.optionsPanel.hidden) card.setExpanded(false);
        });
      }
      if (this.closeButton) this.closeButton.hidden = !expanded;
      this.toggleButton?.setAttribute('aria-expanded', String(expanded));
      this.optionsPanel.hidden = !expanded;
      (expanded ? this.optionsPanel : this.summary).append(this.openButton);
      this.openButton.classList.toggle('juli-upsell-card__submit', expanded);
      if (this.variants.length > 1) this.openButton.setAttribute('aria-expanded', String(expanded));
      this.updateOptions();
    }
    selections() { return this.fields.map(field => field.querySelector('input:checked')?.value ?? null); }
    selectedVariant() {
      if (this.variants.length === 1) return this.variants[0];
      const selected = this.selections();
      return this.variants.find(v => v.options.every((value, i) => selected[i] === value));
    }
    updateOptions() {
      // One Size needs no customer decision; do not auto-pick an ordinary
      // size just because it is the only size currently in stock.
      this.fields.forEach(field => {
        const inputs = [...field.querySelectorAll('input')];
        if (field.hasAttribute('data-size-option') && inputs.length === 1 &&
            /^one[\s-]*size$/i.test(inputs[0].value.trim())) {
          inputs[0].checked = true;
          inputs[0].hidden = true;
          field.classList.add('juli-upsell-card__fieldset--onesize');
          field.querySelector('legend').hidden = true;
        }
      });
      const selected = this.selections();
      this.fields.forEach((field, index) => {
        field.querySelectorAll('input').forEach(input => {
          const available = this.variants.some(v => v.available && v.options[index] === input.value && v.options.every((value, i) => i === index || selected[i] === null || (!this.fields[i].hasAttribute('data-color-option') && (field.hasAttribute('data-color-option') || i > index)) || selected[i] === value));
          input.disabled = this.busy || !available;
          input.title = available ? '' : this.dataset.soldOut;
        });
      });
      this.fields.forEach((field, index) => {
        const label = field.querySelector('[data-color-label]');
        if (label) label.textContent = selected[index] || label.dataset.defaultLabel;
      });
      const variant = this.selectedVariant();
      this.querySelectorAll('[data-product-link]').forEach(link => {
        if (!link.dataset.productUrl) link.dataset.productUrl = link.getAttribute('href');
        const url = new URL(link.dataset.productUrl, window.location.href);
        if (variant) url.searchParams.set('variant', variant.id);
        link.href = url.pathname + url.search + url.hash;
      });
      this.openButton.disabled = this.busy || (this.optionsPanel.hidden
        ? !this.variants.some(v => v.available)
        : !variant?.available);
      // Once a colour is chosen show its image; price only changes for a fully
      // selected variant so no unchosen size's price is presented as final.
      const partial = this.variants.find(v => v.available && v.options.every((value, i) => selected[i] === null || selected[i] === value));
      const image = this.querySelector('.juli-upsell-card__image img');
      const imageUrl = (variant || partial)?.image;
      if (image && imageUrl) { image.src = imageUrl; image.removeAttribute('srcset'); }
      if (variant) this.querySelector('[data-price]').textContent = variant.price;
      else if (this.initialPrice !== undefined) this.querySelector('[data-price]').textContent = this.initialPrice;
      if (this.initialPrice === undefined) this.initialPrice = this.querySelector('[data-price]').textContent;
    }
    clearError() { this.errorMessage.hidden = true; this.errorMessage.textContent = ''; }
    async add(variant) {
      if (this.busy) return;
      if (!variant?.available) {
        this.errorMessage.textContent = this.dataset.choose;
        this.errorMessage.hidden = false;
        return;
      }
      this.busy = true;
      const focusOrigin = document.activeElement;
      this.clearError();
      const button = this.variants.length === 1 ? this.openButton : this.addButton;
      this.openButton.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      this.updateOptions();
      try {
        const cart = await cartReady();
        const result = await new Promise((resolve, reject) => {
          try {
            cart.add({ items: [{ id: variant.id, quantity: 1 }] }, {
              info: { juliInlineUpsell: true },
              firstCallback: (state) => {
                if (state.responseData?.ok) {
                  window.JuliUpsellFocus.queue(this, focusOrigin);
                  window.JuliCartRecommendations?.markAdded(Number(this.dataset.productId));
                }
              },
              lastCallback: resolve
            });
          } catch (error) { reject(error); }
        });
        if (!result.responseData?.ok) {
          const description = result.responseData?.body?.description;
          throw new Error(typeof description === 'string' ? description : this.dataset.error);
        }
        this.setExpanded(false);
        // The cart library updates the drawer and count. No success label.
        this.restoreFocus = true;
      } catch (error) {
        this.errorMessage.textContent = error.message === 'Cart unavailable' ? this.dataset.error : (error.message || this.dataset.error);
        this.errorMessage.hidden = false;
      } finally {
        this.busy = false;
        this.openButton.disabled = !this.variants.some(v => v.available);
        button.removeAttribute('aria-busy');
        this.status.textContent = '';
        this.updateOptions();
        if (this.restoreFocus && this.isConnected) this.openButton.focus({ preventScroll: true });
        this.restoreFocus = false;
      }
    }
  }
  customElements.define('juli-upsell-card', JuliUpsellCard);
  document.addEventListener('variant:changed', event => {
    const source = event.target;
    if (source?.tagName !== 'VARIANT-SELECTOR') return;
    const section = source.closest('product-information, .shopify-section');
    const upsell = section?.querySelector('.juli-upsell[data-source-color-index]');
    if (!upsell) return;
    const color = event.detail?.variant?.options?.[Number(upsell.dataset.sourceColorIndex)];
    upsell.querySelectorAll('juli-upsell-card').forEach(card => card.matchColor(color));
  });
})();
